// useFogsReturn — web only: receives the "Return to ReNurture" navigation from the full FOGS
// check (https://app.renurture.org/?fogs-done=1&done=1&flagged=a,b) and stores
// { flagged, at } as the user's fogsResult (only section keys ever arrive). The params are then
// stripped from the address bar. The page usually opens in a second tab, so the result is also
// handed to the original tab through localStorage.
//
// Native has no URL return: the in-app browser's Done/X closes it and TriageHome's refresh
// (resume / focus / browserFinished -> fetchMyProgress) picks up a linked user's fogs-v1 wave.
import React from 'react';
import { Capacitor } from '@capacitor/core';
import { useProgress } from './useProgress';
import { parseFogsReturn, stripFogsParams, validFlagged } from '@/services/fogs';
import { trackEvent } from '@/services/metrics';

export const FOGS_HANDOFF_KEY = 'renurture.fogs.return';

export function useFogsReturn(): void {
  const progress = useProgress();
  const setRef = React.useRef(progress.setFogsResult);
  setRef.current = progress.setFogsResult;

  React.useEffect(() => {
    const apply = (url: string | null | undefined): boolean => {
      const r = parseFogsReturn(url);
      if (!r) return false;
      const at = new Date().toISOString();
      setRef.current(r.flagged, at);
      trackEvent('complete', 'screen', 'fogs-return');
      return true;
    };

    if (Capacitor.isNativePlatform()) return;
    {
      const href = window.location.href;
      if (apply(href)) {
        try { window.history.replaceState(window.history.state, '', stripFogsParams(href)); } catch { /* ignore */ }
        try {
          // Hand over to the original tab; the event carries the value, so clear the key at once.
          const r = parseFogsReturn(href);
          if (r) {
            localStorage.setItem(FOGS_HANDOFF_KEY, JSON.stringify({ flagged: r.flagged, at: new Date().toISOString() }));
            localStorage.removeItem(FOGS_HANDOFF_KEY);
          }
        } catch { /* ignore */ }
      }
      const onStorage = (e: StorageEvent) => {
        if (e.key !== FOGS_HANDOFF_KEY || !e.newValue) return;
        try {
          const v = JSON.parse(e.newValue) as { flagged?: string[]; at?: string };
          if (Array.isArray(v.flagged) && typeof v.at === 'string') setRef.current(validFlagged(v.flagged.filter((k) => typeof k === 'string')), v.at);
        } catch { /* ignore */ }
        try { localStorage.removeItem(FOGS_HANDOFF_KEY); } catch { /* ignore */ }
      };
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    }
  }, []);
}
